import { useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function ReportSettingsCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [centers, setCenters] = useState([]);
  const [newCenter, setNewCenter] = useState("");
  const load = () => {
    api.get("/erp/report-settings").then(({ data }) => setS(data)).catch(() => setS({ min_margin_pct: 30, warranty_default_days: 90 }));
    api.get("/erp/cost-centers").then(({ data }) => setCenters(data)).catch(() => setCenters([]));
  };
  useEffect(load, []);
  const save = async () => {
    setBusy(true);
    try { await api.patch("/erp/report-settings", { min_margin_pct: Number(s.min_margin_pct), warranty_default_days: Number(s.warranty_default_days), thermal_width: Number(s.thermal_width || 80) });
      toast.success("Preferências salvas"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const addCenter = async () => {
    const name = newCenter.trim();
    if (!name) return;
    try { const { data } = await api.post("/erp/cost-centers", { name }); setCenters((c) => [...c, data].sort((a, b) => a.name.localeCompare(b.name))); setNewCenter(""); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delCenter = async (id) => {
    if (!window.confirm("Excluir centro de custo?")) return;
    try { await api.delete(`/erp/cost-centers/${id}`); setCenters((c) => c.filter((x) => x.id !== id)); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!s) return null;
  return (
    <div data-testid="report-settings-card" className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7 space-y-5">
      <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Relatórios e alertas</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Margem mínima (%) — alerta na Central</Label>
          <Input data-testid="rs-min-margin" type="number" min="0" max="100" value={s.min_margin_pct}
                 onChange={(e) => setS((p) => ({ ...p, min_margin_pct: e.target.value }))} className="mt-1 bg-zinc-900 border-zinc-800" />
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Garantia padrão (dias)</Label>
          <Input data-testid="rs-warranty" type="number" min="0" max="1095" value={s.warranty_default_days}
                 onChange={(e) => setS((p) => ({ ...p, warranty_default_days: e.target.value }))} className="mt-1 bg-zinc-900 border-zinc-800" />
          <p className="text-[11px] text-zinc-500 mt-1">Usada quando a OS não tem garantia específica. Também pode ser definida por item.</p>
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Impressora térmica (bobina)</Label>
          <div className="mt-1 flex gap-2">
            {[80, 58].map((w) => (
              <button key={w} type="button" data-testid={`rs-thermal-${w}`} onClick={() => setS((p) => ({ ...p, thermal_width: w }))}
                      className={`h-10 px-4 rounded-md border text-sm font-bold ${Number(s.thermal_width || 80) === w ? "border-green-neon text-green-neon bg-green-brand/10" : "border-zinc-800 text-zinc-300"}`}>{w} mm</button>
            ))}
          </div>
        </div>
      </div>
      <Button data-testid="rs-save" onClick={save} disabled={busy} className="h-11 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Salvar</>)}
      </Button>
      <div>
        <h3 className="font-display text-sm font-bold uppercase text-zinc-100 mt-4 mb-2">Centros de custo</h3>
        <p className="text-xs text-zinc-500 mb-2">Aparecem como opção ao lançar uma conta a pagar. Ex.: Oficina, Administrativo, Marketing.</p>
        <div className="flex gap-2 mb-2">
          <Input data-testid="cc-new" value={newCenter} onChange={(e) => setNewCenter(e.target.value)} placeholder="Nome do centro" className="bg-zinc-900 border-zinc-800" />
          <Button data-testid="cc-add" onClick={addCenter} className="bg-green-brand hover:bg-green-brandDark text-white"><Plus className="h-4 w-4" /></Button>
        </div>
        {centers.length === 0 ? <p className="text-sm text-zinc-500">Nenhum centro cadastrado ainda.</p> : (
          <ul className="rounded-lg border border-zinc-800">
            {centers.map((c) => (
              <li key={c.id} data-testid={`cc-row-${c.id}`} className="flex justify-between items-center px-3 py-2 border-t border-zinc-800 first:border-t-0">
                <span className="text-zinc-200">{c.name}</span>
                <button data-testid={`cc-del-${c.id}`} onClick={() => delCenter(c.id)} className="text-zinc-500 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
